import { types as sdkTypes, file as sdkFile } from '../util/sdkLoader';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { MAX_MESSAGE_ATTACHMENTS } from '../util/fileAttachments';

const { UUID } = sdkTypes;

export { MAX_MESSAGE_ATTACHMENTS, MAX_ATTACHMENT_SIZE_BYTES } from '../util/fileAttachments';

// Files staged for a message before it is sent, shared by every composer that
// can attach files: the transaction chat, the enquiry modals and the
// application accept form. Each composer stages under its own scope so chips
// never leak between them (the listing page keeps its enquiry modal mounted
// even while closed, and the transaction page renders two composers).

// ================ Scopes ================ //

export const SCOPE_TRANSACTION_PAGE = 'TransactionPage';
export const SCOPE_LISTING_ENQUIRY = 'ListingPageEnquiry';
export const SCOPE_PROFILE_ENQUIRY = 'ProfilePageEnquiry';
export const SCOPE_APPLICATION_PANEL = 'ApplicationPanel';
// The custom offer form (TransactionPanel modal); files here become the
// transaction's protectedFileAttachments, not a message's.
export const SCOPE_CUSTOM_OFFER = 'CustomOffer';

// ================ Action types ================ //

export const UPLOAD_ATTACHMENT_REQUEST = 'app/messageAttachments/UPLOAD_ATTACHMENT_REQUEST';
export const UPLOAD_ATTACHMENT_PROGRESS = 'app/messageAttachments/UPLOAD_ATTACHMENT_PROGRESS';
export const UPLOAD_ATTACHMENT_SUCCESS = 'app/messageAttachments/UPLOAD_ATTACHMENT_SUCCESS';
export const UPLOAD_ATTACHMENT_ERROR = 'app/messageAttachments/UPLOAD_ATTACHMENT_ERROR';
export const REMOVE_ATTACHMENT = 'app/messageAttachments/REMOVE_ATTACHMENT';
export const CLEAR_STAGED_ATTACHMENTS = 'app/messageAttachments/CLEAR_STAGED_ATTACHMENTS';
export const SEED_STAGED_ATTACHMENTS = 'app/messageAttachments/SEED_STAGED_ATTACHMENTS';

// ================ Reducer ================ //

// stagedByScope: { [scope]: { [tempId]: entry } }, keyed by tempId in
// insertion order (= staging order). Entry:
// { tempId, fileId, name, size, progress, state: 'uploading'|'pendingVerification'|'available'|'failed', error }
const initialState = {
  stagedByScope: {},
};

const withScope = (state, scope, scoped) => ({
  ...state,
  stagedByScope: { ...state.stagedByScope, [scope]: scoped },
});

// The user may remove a staged attachment while its upload is still in
// flight, so late progress/success/error actions for removed entries no-op.
const updateEntry = (state, scope, tempId, update) => {
  const scoped = state.stagedByScope[scope] || {};
  const entry = scoped[tempId];
  if (!entry) {
    return state;
  }
  return withScope(state, scope, { ...scoped, [tempId]: { ...entry, ...update } });
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_ATTACHMENT_REQUEST: {
      const { scope, tempId, name, size } = payload;
      const entry = {
        tempId,
        fileId: null,
        name,
        size,
        progress: 0,
        state: 'uploading',
        error: null,
      };
      const scoped = state.stagedByScope[scope] || {};
      return withScope(state, scope, { ...scoped, [tempId]: entry });
    }
    case UPLOAD_ATTACHMENT_PROGRESS:
      return updateEntry(state, payload.scope, payload.tempId, { progress: payload.progress });
    case UPLOAD_ATTACHMENT_SUCCESS:
      return updateEntry(state, payload.scope, payload.tempId, {
        fileId: payload.fileId,
        state: 'pendingVerification',
        progress: 100,
      });
    case UPLOAD_ATTACHMENT_ERROR:
      return updateEntry(state, payload.scope, payload.tempId, {
        state: 'failed',
        error: payload.error,
      });
    case REMOVE_ATTACHMENT: {
      const { scope, tempId } = payload;
      const { [tempId]: removed, ...scoped } = state.stagedByScope[scope] || {};
      return withScope(state, scope, scoped);
    }
    case SEED_STAGED_ATTACHMENTS: {
      const { scope, entries } = payload;
      const scoped = entries.reduce((acc, entry) => ({ ...acc, [entry.tempId]: entry }), {});
      return withScope(state, scope, scoped);
    }
    case CLEAR_STAGED_ATTACHMENTS:
      return withScope(state, payload.scope, {});

    default:
      return state;
  }
}

// ================ Selectors ================ //

// Staged attachments of one composer as an array in staging order.
export const stagedAttachmentsList = (state, scope) =>
  Object.values(state.messageAttachments.stagedByScope[scope] || {});

// File ids that finished uploading and can be attached to an outgoing message.
export const readyAttachmentFileIds = list =>
  list
    .filter(a => a.fileId && (a.state === 'pendingVerification' || a.state === 'available'))
    .map(a => a.fileId);

// True while sending must wait: in-flight uploads have no file id yet, and
// failed uploads must be removed by the user first.
export const hasBlockingAttachments = list =>
  list.some(a => a.state === 'uploading' || a.state === 'failed');

// The `publicFileAttachments` param for sdk.messages.send, or nothing when
// there are no files.
export const publicFileAttachmentsMaybe = (fileIds = []) =>
  fileIds.length > 0
    ? { publicFileAttachments: fileIds.map(fileId => ({ fileId: new UUID(fileId) })) }
    : {};

// The `protectedFileAttachments` transition param read by
// :action/update-protected-files. Always an array: an empty one deliberately
// detaches every file, so callers must only pass it on processes that run the
// action (see offerAttachmentsSupported in util/customOffer.js).
export const protectedFileAttachmentsParam = (fileIds = []) => ({
  protectedFileAttachments: fileIds.map(fileId => ({ fileId: new UUID(fileId) })),
});

// Staged entries for files already attached to a resource, so an existing set
// (an offer being updated) can be edited like freshly uploaded files. The file
// id doubles as tempId: it is unique, and removing the chip drops the file from
// the next protectedFileAttachments set. A file whose scan failed is staged as
// failed so the user has to remove it before sending.
export const stagedEntriesFromAttachments = attachments =>
  attachments.map(attachment => {
    const { id, attributes } = attachment.file;
    const state =
      attributes.state === 'available'
        ? 'available'
        : attributes.state === 'verificationFailed'
        ? 'failed'
        : 'pendingVerification';
    return {
      tempId: id.uuid,
      fileId: id.uuid,
      name: attributes.name,
      size: attributes.size,
      progress: 100,
      state,
      error: null,
    };
  });

// ================ Action creators ================ //

export const uploadAttachmentRequest = ({ scope, tempId, name, size }) => ({
  type: UPLOAD_ATTACHMENT_REQUEST,
  payload: { scope, tempId, name, size },
});
export const uploadAttachmentProgress = (scope, tempId, progress) => ({
  type: UPLOAD_ATTACHMENT_PROGRESS,
  payload: { scope, tempId, progress },
});
export const uploadAttachmentSuccess = (scope, tempId, fileId) => ({
  type: UPLOAD_ATTACHMENT_SUCCESS,
  payload: { scope, tempId, fileId },
});
export const uploadAttachmentError = (scope, tempId, error) => ({
  type: UPLOAD_ATTACHMENT_ERROR,
  error: true,
  payload: { scope, tempId, error },
});
export const removeAttachment = (scope, tempId) => ({
  type: REMOVE_ATTACHMENT,
  payload: { scope, tempId },
});
export const clearStagedAttachments = scope => ({
  type: CLEAR_STAGED_ATTACHMENTS,
  payload: { scope },
});
export const seedStagedAttachments = (scope, entries) => ({
  type: SEED_STAGED_ATTACHMENTS,
  payload: { scope, entries },
});

// ================ Thunks ================ //

// Upload one file to Sharetribe storage so it can be attached to a message:
// ownFiles.create (validates size/mime) -> fileUploads.create (presigned URL)
// -> direct PUT to storage. The file is attachable once the upload completes;
// Sharetribe scans it in the background ('pendingVerification' -> 'available').
export const uploadAttachment = (scope, browserFile) => (dispatch, getState, sdk) => {
  if (stagedAttachmentsList(getState(), scope).length >= MAX_MESSAGE_ATTACHMENTS) {
    return Promise.resolve();
  }

  const tempId = `${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
  dispatch(
    uploadAttachmentRequest({ scope, tempId, name: browserFile.name, size: browserFile.size })
  );

  // Removing a staged chip mid-upload just abandons the file; unattached
  // Sharetribe files are deleted automatically after one hour.
  const stillStaged = () => !!(getState().messageAttachments.stagedByScope[scope] || {})[tempId];

  return sdk.ownFiles
    .create(sdkFile.metadata(browserFile))
    .then(response => {
      const fileId = response.data.data.id;
      if (!stillStaged()) {
        return null;
      }
      return sdk.fileUploads.create({ fileId }).then(uploadResponse => {
        const { url, method, headers } = uploadResponse.data.data.attributes;
        if (!stillStaged()) {
          return null;
        }
        return sdkFile
          .upload({
            method,
            url,
            headers,
            file: browserFile,
            onUploadProgress: progressEvent => {
              const { loaded, total } = progressEvent || {};
              if (total) {
                const progress = Math.min(100, Math.round((loaded / total) * 100));
                dispatch(uploadAttachmentProgress(scope, tempId, progress));
              }
            },
          })
          .then(() => dispatch(uploadAttachmentSuccess(scope, tempId, fileId.uuid)));
      });
    })
    .catch(e => {
      log.error(e, 'upload-message-attachment-failed', { fileName: browserFile.name, scope });
      dispatch(uploadAttachmentError(scope, tempId, storableError(e)));
    });
};
